import { useRef, useState } from 'react'
import { api } from '../api/client'
import type { MibSummary } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { Card, ErrorBanner, Spinner } from './common'
import { DataTable, type Column } from './DataTable'

/**
 * Vendor MIBs uploaded by the tenant (Network page).
 *
 * Only the product registrations matter (one OID per model, what an agent
 * returns as sysObjectID): they name the tenant's devices at once and are
 * proposed to the platform administration for every tenant. Comments and
 * quoted texts are dropped in the browser before sending — the server keeps
 * only OID definitions, and the WAF blocks a raw MIB whose descriptions look
 * like commands or SQL (tried against the real image, 02/10/2026).
 */

/** The MIB without comments and quoted texts (same rule as the server). */
export function stripMib(text: string): string {
  return text.replace(/"[^"]*"|--[^\n]*?(?:--|$)/gm, (m) => (m.startsWith('"') ? '""' : ' '))
}

export function MibProdotto() {
  const { principal } = useAuth()
  const ruoli = principal?.roles ?? []
  const admin = ruoli.includes('tenant_admin') || ruoli.includes('provider_admin')
  const dati = useAsync(() => api.mibs(), [])
  const file = useRef<HTMLInputElement>(null)
  const [lavoro, setLavoro] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState<string | null>(null)

  async function carica(files: FileList | null) {
    if (!files || files.length === 0) return
    setErrore(null)
    setEsito(null)
    const scartati: string[] = []
    let caricati = 0
    for (const f of Array.from(files)) {
      setLavoro(t('Carico {f}…', { f: f.name }))
      try {
        await api.uploadMib(f.name, stripMib(await f.text()))
        caricati += 1
      } catch {
        scartati.push(f.name)
      }
    }
    setLavoro(null)
    setEsito(scartati.length
      ? t('Caricati {n} MIB; non letti: {s}.', { n: caricati, s: scartati.join(', ') })
      : t('Caricati {n} MIB.', { n: caricati }))
    if (file.current) file.current.value = ''
    dati.reload()
  }

  async function togli(id: string, modulo: string) {
    if (!window.confirm(t('Togliere il MIB {m}? I modelli che dava non si riconoscono più in questo tenant.', { m: modulo }))) return
    try {
      await api.deleteMib(id)
      dati.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  const d = dati.data
  const colonne: Column<MibSummary['modules'][number]>[] = [
    { key: 'module', header: t('Modulo'), mono: true },
    { key: 'file_name', header: t('File'), render: (m) => <small>{m.file_name}</small> },
    { key: 'products', header: t('Modelli'), type: 'number', align: 'left' },
    { key: 'uploaded_at', header: t('Caricato'), type: 'date', value: (m) => m.uploaded_at ?? '', render: (m) => (
      <small>{m.uploaded_by}{m.uploaded_at && ` · ${new Date(m.uploaded_at).toLocaleDateString(localeCorrente())}`}</small>
    ) },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, render: (m) => (
      <div className="rowactions">
        {admin && <button type="button" className="secondary" onClick={() => void togli(m.id, m.module)}>{t('Elimina')}</button>}
      </div>
    ) },
  ]
  return (
    <Card
      title={t('MIB di prodotto ({n})', { n: d?.modules.length ?? 0 })}
      actions={admin && (
        <>
          <input ref={file} type="file" multiple accept=".mib,.my,.txt,.smi,text/plain" style={{ display: 'none' }}
                 onChange={(e) => void carica(e.target.files)} aria-label={t('Scegli i file MIB')} />
          <button type="button" onClick={() => file.current?.click()} disabled={lavoro !== null}>{t('Carica MIB')}</button>
        </>
      )}
    >
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Dai MIB dei costruttori CENSO prende solo le registrazioni di prodotto: un OID per modello, quello che l’apparato dichiara in sysObjectID. Riconoscono subito gli apparati di questo tenant e vengono proposte all’amministrazione della piattaforma per tutti i tenant. Commenti e descrizioni si tolgono prima dell’invio; del file si conservano solo le definizioni degli OID. Si scaricano dal portale di supporto del costruttore: servono i MIB «…-PRODUCTS-MIB» o «…-SMI» e quello che li importa.')}
      </p>
      <ErrorBanner error={dati.error} />
      <ErrorBanner error={errore} />
      {lavoro && <p className="field__hint"><Spinner /> {lavoro}</p>}
      {esito && <p className="field__hint">{esito}</p>}
      {d && (
        <p className="field__hint">
          {t('Modelli riconoscibili dai MIB caricati: {n}.', { n: d.products.toLocaleString(localeCorrente()) })}
          {d.unresolved > 0 && ` ${t('{n} definizioni restano senza OID perché manca il MIB che definisce il loro nodo padre.', { n: d.unresolved })}`}
        </p>
      )}
      {d && d.missing_modules.length > 0 && (
        <div className="banner banner--info">
          {t('Da caricare: {m}.', {
            m: d.missing_modules.map((m) => `${m.module || t('modulo ignoto')} (${m.symbols.join(', ')})`).join(' · '),
          })}
        </div>
      )}
      {d && d.modules.length > 0 && (
        <DataTable rows={d.modules} columns={colonne} rowKey={(m) => m.id} conteggio={{ nome: t('moduli') }} />
      )}
    </Card>
  )
}
